import React, {useMemo} from 'react';
import {Descriptions, Empty, Table, Tag} from 'antd';
import moment from 'moment';
import {formatCurrencyByLocalChapter} from '../../../Util/commonUtility';
import {transformAuditLogValue} from '../../../Util/auditLogValueTransform';

const REFUND_ACTION_COLORS = {
  'Refund Requested': 'orange',
  'Refund Approved': 'green',
  'Refund Pending Approval': 'gold',
  'Refund Cancelled': 'red',
  'Refund Completed': 'blue',
};

const formatAmount = (value, chapterData) => {
  if (value == null || value === '') return '-';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numeric)) return value;
  return formatCurrencyByLocalChapter(numeric, chapterData);
};

const RefundAuditLogView = ({
  histories,
  totalCount,
  currentPage,
  pageCount,
  onPageChange,
  userDetails,
  transformOptions,
  localChapterData,
}) => {
  const sortedHistories = useMemo(
    () =>
      [...histories].sort((a, b) => {
        const timeDiff =
          new Date(b?.created_at || 0).getTime() -
          new Date(a?.created_at || 0).getTime();
        if (timeDiff !== 0) return timeDiff;
        return (b?.id || 0) - (a?.id || 0);
      }),
    [histories],
  );

  const formatUserName = (userId) => {
    if (userId == null || userId === '') return '-';
    if (typeof userId === 'string' && Number.isNaN(Number(userId))) {
      return userId;
    }
    const numericId = typeof userId === 'string' ? Number(userId) : userId;
    const user = userDetails?.data?.find((item) => item.id === numericId);
    if (!user) return `User ${numericId}`;
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return name || user.email || `User ${numericId}`;
  };

  const formatPerformedBy = (history) => {
    if (history?.modified_by_role === 'System') return 'System';
    return formatUserName(history?.modified_by_id);
  };

  const getEventSnapshot = (history) =>
    transformAuditLogValue(history?.new_value || {}, 'EVENT_REFUND', transformOptions);

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '16%',
      render: (date) =>
        date ? moment(date).format('DD MMM YYYY, hh:mm A') : '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: '18%',
      render: (_, row) => {
        const snapshot = getEventSnapshot(row);
        const action = snapshot?.action || 'Refund Event';
        return (
          <Tag color={REFUND_ACTION_COLORS[action] || 'default'}>{action}</Tag>
        );
      },
    },
    {
      title: 'Refund %',
      key: 'refund_percentage',
      width: '10%',
      render: (_, row) => getEventSnapshot(row)?.refund_percentage ?? '-',
    },
    {
      title: 'Mode',
      key: 'refund_mode',
      width: '10%',
      render: (_, row) => getEventSnapshot(row)?.refund_mode ?? '-',
    },
    {
      title: 'Amount',
      key: 'refunded_amount',
      width: '12%',
      render: (_, row) =>
        formatAmount(getEventSnapshot(row)?.refunded_amount, localChapterData),
    },
    {
      title: 'Approved By',
      key: 'approved_by_id',
      width: '14%',
      render: (_, row) => {
        const approvedBy = getEventSnapshot(row)?.approved_by_id;
        return approvedBy ? formatUserName(approvedBy) : '-';
      },
    },
    {
      title: 'Performed By',
      key: 'performed_by',
      width: '14%',
      render: (_, row) => formatPerformedBy(row),
    },
  ];

  return (
    <>
      <div style={{marginBottom: 12, fontSize: 12, color: '#666'}}>
        Refund audit log — each row is a refund event (request, approval,
        cancellation, or completion).
      </div>
      <Table
        size='small'
        rowKey='id'
        columns={columns}
        dataSource={sortedHistories}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='No refund activity recorded yet'
            />
          ),
        }}
        expandable={{
          expandedRowRender: (row) => {
            const snapshot = getEventSnapshot(row);
            return (
              <Descriptions size='small' column={2} bordered>
                <Descriptions.Item label='Attendee'>
                  {[snapshot?.attendee_first_name, snapshot?.attendee_last_name]
                    .filter(Boolean)
                    .join(' ') || '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Email'>
                  {snapshot?.attendee_email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Registration Status'>
                  {snapshot?.registration_status || '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Total Cost'>
                  {formatAmount(snapshot?.total_cost, localChapterData)}
                </Descriptions.Item>
                <Descriptions.Item label='Paid Amount'>
                  {formatAmount(snapshot?.paid_amount, localChapterData)}
                </Descriptions.Item>
                <Descriptions.Item label='Credit Applied'>
                  {formatAmount(snapshot?.credit_amount, localChapterData)}
                </Descriptions.Item>
                <Descriptions.Item label='Discount'>
                  {formatAmount(snapshot?.discount_amount, localChapterData)}
                </Descriptions.Item>
                <Descriptions.Item label='Performed By'>
                  {formatPerformedBy(row)}
                </Descriptions.Item>
              </Descriptions>
            );
          },
          rowExpandable: (row) => Boolean(row?.new_value),
        }}
        pagination={{
          current: currentPage,
          pageSize: pageCount,
          total: totalCount,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
      />
      <div style={{fontSize: 12, color: '#666', marginTop: 6}}>
        Total events: {totalCount}
      </div>
    </>
  );
};

export default RefundAuditLogView;
